<template>
  <div :class="['input-container', type, { error: isError }]">
    <div class="input-wrapper">
      <input
        :id="name"
        :type="setInputType"
        v-model="computedValue"
        class="input"
        :placeholder="placeholder"
        :disabled="isDisabled"
        @focus="emit('inputFocus')"
        @blur="emit('inputBlur')"
        @keyup.enter="submit"
      />
      <label class="floating-label" :for="name">{{ placeholder }}</label>
      <UiIcon
        v-if="type === 'filter'"
        name="search"
        size="20px"
        class="filter-icon"
        color-fill="var(--state-color)"
      />
    </div>
    <span v-if="supportingText" class="body-s supporting-text">{{
      supportingText
    }}</span>
    <div v-if="$slots.supportingText" class="body-s supporting-text">
      <slot name="supportingText" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import type { WritableComputedRef } from 'vue'
import type { PropsInput } from './types'
import UiIcon from '@sephora-asia/ui-icon/UiIcon.vue'

const props = withDefaults(defineProps<PropsInput>(), {
  isDisabled: false,
})

const emit = defineEmits<{
  (e: 'input', value: string | number): void
  (e: 'inputFocus'): void
  (e: 'inputBlur'): void
  (e: 'submit'): void
}>()

const setInputType = computed<string>(() => {
  if (props.type === 'filter') {
    return 'text'
  }
  return props.type
})

const newValue = ref<string | number>(props.value)
const computedValue: WritableComputedRef<string | number> = computed({
  get(): string | number {
    return newValue.value
  },
  set(value: string | number): void {
    newValue.value = value
    emit('input', value)
  },
})

watch(
  () => props.value,
  (propsVal: string | number) => {
    newValue.value = propsVal
  }
)

function submit() {
  emit('submit')
}
</script>

<style lang="scss" scoped>
.input-container {
  --state-color: var(--color-base-grey-30);
  display: flex;
  flex-direction: column;
  gap: 2px;

  &.error {
    .supporting-text,
    .input,
    .floating-label,
    .input:focus {
      color: var(--color-base-red-n100);
    }

    .input {
      border: 1px solid var(--color-base-red-n100);
    }
  }

  .input {
    border-top-right-radius: var(--size-radius-medium);
    border-bottom-right-radius: var(--size-radius-medium);
  }

  .floating-label {
    opacity: 0;
  }

  &:not(.filter) {
    .floating-label {
      opacity: 1;
    }

    .input {
      padding-top: 12px;

      &::placeholder {
        opacity: 0;
      }
    }
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;

    .filter-icon {
      position: absolute;
      right: 12px;
    }
  }

  .input {
    @extend .body-m;
    box-sizing: border-box;
    width: 100%;
    height: 40px;
    padding-left: 12px;
    padding-right: 12px;
    background: var(--color-base-white);
    border: 1px solid var(--state-color);
    transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
    border-top-left-radius: var(--size-radius-medium);
    border-bottom-left-radius: var(--size-radius-medium);
    color: var(--color-base-black);

    &:focus {
      outline: none;
      color: var(--color-base-black);
    }

    &:disabled {
      --state-color: var(--color-base-grey-30);
      background-color: var(--color-base-grey-10);
    }

    &:focus,
    &:not(:placeholder-shown) {
      --state-color: var(--color-base-black);
      & + .floating-label {
        @extend .body-s;
        top: 0;
      }

      & ~ :deep(.filter-icon) svg {
        fill: var(--color-base-black);
      }
    }
  }

  .floating-label {
    @extend .body-m;
    color: var(--color-base-grey-50);
    transition: 0.2s ease all;
    position: absolute;
    top: 8px;
    left: 12px;
    pointer-events: none;
  }
}
</style>
